import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Button } from '@mui/material';

export type ConfirmColor = 'error' | 'warning' | 'primary' | 'success';

export interface ConfirmOptions {
  title?: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: ConfirmColor;
  destructive?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingState {
  open: boolean;
  opts: ConfirmOptions;
}

const initial: PendingState = { open: false, opts: {} };

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<PendingState>(initial);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, opts });
    });
  }, []);

  const finish = useCallback((ok: boolean) => {
    setState({ open: false, opts: {} });
    const resolver = resolverRef.current;
    resolverRef.current = null;
    if (resolver) resolver(ok);
  }, []);

  const {
    title = 'Confirm',
    body = 'Are you sure?',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    confirmColor,
    destructive,
  } = state.opts;

  const color: ConfirmColor = confirmColor || (destructive ? 'error' : 'primary');

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog open={state.open} onClose={() => finish(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
        <DialogContent>
          {typeof body === 'string' ? (
            <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{body}</DialogContentText>
          ) : (
            body
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => finish(false)} color="inherit">{cancelLabel}</Button>
          <Button onClick={() => finish(true)} color={color} variant="contained" autoFocus>
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
};

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    return (opts) => Promise.resolve(window.confirm(typeof opts.body === 'string' ? opts.body : (opts.title || 'Confirm?')));
  }
  return ctx;
}
